import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AssistantRuntimeProvider,
  type ThreadMessage,
  type ThreadMessageLike,
  unstable_useRemoteThreadListRuntime,
  useLocalRuntime,
} from "@assistant-ui/react";
import { CuadraChatClient, setFileAuthConfig } from "../lib/cuadraChatClient";
import { createChatModelAdapter } from "../adapters/chatModelAdapter";
import { backgroundGenerationStore } from "../adapters/backgroundGenerationStore";
import type { ToolDefinition } from "../types/cuadra";
import {
  createThreadListAdapter,
  messageIdToChatIdMap,
} from "../adapters/threadListAdapter";
import {
  type AttachmentValidationError,
  createAttachmentAdapter,
} from "../adapters/attachmentAdapter";
import { attachmentErrorStore } from "../adapters/attachmentErrorStore";
import { useSingleChatLoader } from "../hooks/useSingleChatLoader";
import { useResolvedSingleChatServerThreadId } from "../hooks/useResolvedSingleChatServerThreadId";
import { ArtifactLoaderContext } from "../contexts/ArtifactLoaderContext";
import { CuadraErrorSurfaceContext } from "../contexts/CuadraErrorSurfaceContext";
import {
  SourceFeedbackContext,
  type SourceFeedbackContextValue,
  type SourceRating,
} from "../contexts/SourceFeedbackContext";
import {
  MessageFeedbackContext,
  type MessageFeedbackContextValue,
} from "../contexts/MessageFeedbackContext";

/** Compatible with assistant-ui's FeedbackAdapter (not re-exported from the public API). */
type MessageFeedbackAdapter = {
  submit: (feedback: {
    message: ThreadMessage;
    type: "positive" | "negative";
  }) => void;
};

export interface CuadraRuntimeProviderProps {
  children: React.ReactNode;
  baseUrl: string;
  sessionToken?: string | null;
  isProxyMode?: boolean; // If true, uses proxy URL patterns (removes /v1 prefix)

  // Mode selection
  mode?: "singleChat" | "multiChat"; // Default: 'multiChat'

  // Model configuration
  modelMode?: "selector" | "fixed"; // Default: 'fixed'
  modelId?: string; // Required if modelMode === 'fixed'
  onModelChange?: (modelId: string) => void; // Called when model changes (selector mode)

  // Chat configuration
  ephemeral?: boolean; // Default: false - creates temporary chats that auto-delete
  systemPrompt?: string;
  /** Enable reasoning/thinking output for supported models (e.g., o1, o3) */
  enableReasoning?: boolean;
  /** Enable AI artifact panel (documents, code, HTML, SVG, diagrams) */
  enableArtifacts?: boolean;

  /**
   * Optional tool definitions to pass through to the chat API.
   * Intended for host apps (e.g. dashboard) to provide lightweight routing tools.
   */
  tools?: ToolDefinition[];
  /**
   * Optional callback invoked when the model emits a non-artifact tool call.
   */
  onToolCall?: (
    toolName: string,
    args: Record<string, unknown>,
    ctx?: { lastUserText: string; modelId?: string },
  ) => void;

  // Thread configuration
  initialThreadId?: string; // Load existing thread (singleChat fetches messages; multiChat uses thread list)

  // Callbacks
  onError?: (error: Error) => void;
  onChatCreated?: (chatId: string) => void;
  onUserMessage?: () => void; // Called when a user message is sent
  onThreadIdUpdate?: (oldThreadId: string, newThreadId: string) => void; // Callback when thread ID is updated from local to server
  onChatsLoaded?: () => void; // Callback when chats/thread list is loaded
  /**
   * Called when an existing chat is loaded so the parent can sync
   * its model-selector UI *without* triggering the full onModelChange chain.
   */
  onModelSelectorSync?: (modelId: string) => void;
  /** Enable file attachments in the chat input */
  enableAttachments?: boolean;
  /** Whether the current model supports vision/image inputs. When false, image attachments are rejected. */
  supportsVision?: boolean;
  /** Interceptor called before each API request. Return true to allow, false to block. */
  onBeforeRequest?: () => boolean | Promise<boolean>;
  /**
   * Mock response to show when onBeforeRequest returns false.
   * If set, instead of throwing an error, this message will be streamed as a response.
   * Can be a string or a function that returns a string (called each time a request is blocked).
   * Useful for showing helpful messages like "Complete setup before chatting".
   */
  mockResponseOnBlocked?: string | (() => string);
  /**
   * Called when a 401 Unauthorized response is received from the API.
   * The parent app should use this to trigger a token refresh cycle
   * and update the sessionToken prop with a fresh value.
   */
  onUnauthorized?: () => void;
  /**
   * Called when a chat response stream finishes (assistant reply complete).
   * Use to refresh usage/credits after each response.
   */
  onStreamEnd?: (chatId: string) => void;
  /** Called when artifact generation begins (create_artifact tool call detected). */
  onArtifactGenerating?: (chatId: string) => void;
  /** Called when artifact generation completes (stream or background polling). */
  onArtifactComplete?: (chatId: string, artifactId?: string) => void;
  /**
   * When artifacts are enabled, open this artifact (server id or identifier) after list load.
   * Host apps often mirror this from a query param, e.g. `?artifact=`.
   */
  initialOpenArtifactId?: string;
  /**
   * Stable id for this runtime so global error stores (e.g. attachment errors) only affect
   * this chat surface. When omitted, a React `useId()` value is used.
   */
  errorSurfaceId?: string;
}

/**
 * Main provider component that sets up assistant-ui runtime with Cuadra API
 * Supports single/multi chat modes, fixed/selector model modes, and ephemeral chats
 */
export function CuadraRuntimeProvider({
  children,
  baseUrl,
  sessionToken,
  isProxyMode = false,
  mode = "multiChat",
  modelId,
  onModelChange: _onModelChange,
  ephemeral = false,
  systemPrompt,
  enableReasoning = false,
  enableArtifacts = false,
  tools: chatTools,
  onToolCall,
  initialThreadId,
  onError,
  onChatCreated,
  onUserMessage,
  onThreadIdUpdate,
  onChatsLoaded,
  onModelSelectorSync,
  enableAttachments = false,
  supportsVision = true,
  onBeforeRequest,
  mockResponseOnBlocked,
  onUnauthorized,
  onStreamEnd: onStreamEndProp,
  onArtifactGenerating: onArtifactGeneratingProp,
  onArtifactComplete: onArtifactCompleteProp,
  initialOpenArtifactId,
  errorSurfaceId: errorSurfaceIdProp,
}: CuadraRuntimeProviderProps) {
  const generatedErrorSurfaceId = useId();
  const errorSurfaceId =
    errorSurfaceIdProp && errorSurfaceIdProp.trim() !== ""
      ? errorSurfaceIdProp
      : generatedErrorSurfaceId;

  // Create API client once per baseUrl/proxyMode.  The session token is
  // updated via setSessionToken() so a refresh never recreates the client
  // (which would tear down the entire runtime and blink the UI mid-stream).
  const client = useMemo(
    () => new CuadraChatClient(baseUrl, sessionToken || undefined, isProxyMode),
    [baseUrl, isProxyMode],
  );

  useEffect(() => {
    client.setSessionToken(sessionToken || null);
  }, [client, sessionToken]);

  // Keep the onUnauthorized callback in sync without recreating the client.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  useEffect(() => {
    client.setOnUnauthorized(() => onUnauthorizedRef.current?.());
    return () => client.setOnUnauthorized(null);
  }, [client]);

  // Publish auth config so rendering components (e.g. AuthenticatedImage) can
  // fetch file content with proper credentials.
  useEffect(() => {
    setFileAuthConfig({
      baseUrl,
      sessionToken: sessionToken || undefined,
      isProxyMode: isProxyMode ?? false,
    });
    return () => setFileAuthConfig(null);
  }, [baseUrl, sessionToken, isProxyMode]);

  // Track selected model ID (syncs with prop)
  const [selectedModelId, setSelectedModelId] = useState<string | null>(
    modelId || null,
  );

  // Sync selectedModelId with modelId prop
  useEffect(() => {
    if (modelId) {
      setSelectedModelId(modelId);
    }
  }, [modelId]);

  // Stable ref so handleChatCreated never changes identity — this prevents
  // modelAdapter from being recreated on every render when the prop
  // reference shifts (e.g. through normalizeCuadraChatProps).
  const onChatCreatedPropRef = useRef(onChatCreated);
  onChatCreatedPropRef.current = onChatCreated;

  const refreshCallbackRef = useRef<((chatId: string) => void) | null>(null);
  const currentChatIdRef = useRef<string | null>(null);

  const onStreamEndPropRef = useRef(onStreamEndProp);
  onStreamEndPropRef.current = onStreamEndProp;

  const onArtifactGeneratingRef = useRef(onArtifactGeneratingProp);
  onArtifactGeneratingRef.current = onArtifactGeneratingProp;

  const onArtifactCompleteRef = useRef(onArtifactCompleteProp);
  onArtifactCompleteRef.current = onArtifactCompleteProp;

  const handleStreamEnd = useCallback((chatId: string) => {
    refreshCallbackRef.current?.(chatId);
    onStreamEndPropRef.current?.(chatId);
  }, []);

  // Background-generation reload: when polling detects completion, store the
  // server chatId and bump a key so useSingleChatLoader re-fetches messages.
  // The chatId is needed for new chats where initialThreadId is undefined.
  const [bgReload, setBgReload] = useState<{
    key: number;
    chatId: string | null;
  }>({ key: 0, chatId: null });
  useEffect(() => {
    backgroundGenerationStore.setReloadHandler((chatId: string) => {
      setBgReload((prev) => ({ key: prev.key + 1, chatId }));
    });
    return () => backgroundGenerationStore.setReloadHandler(null);
  }, []);

  const { serverThreadId, onServerThreadCreated } =
    useResolvedSingleChatServerThreadId({
      initialThreadId,
      reloadKey: bgReload.key,
      reloadChatId: bgReload.chatId,
    });

  const handleChatCreated = useCallback(
    (chatId: string) => {
      currentChatIdRef.current = chatId;
      onServerThreadCreated(chatId);
      onChatCreatedPropRef.current?.(chatId);
    },
    [onServerThreadCreated],
  );

  // Handle thread ID update
  const handleThreadIdUpdate = useCallback(
    (oldId: string, newId: string) => {
      onThreadIdUpdate?.(oldId, newId);
    },
    [onThreadIdUpdate],
  );

  // For singleChat mode, create model adapter and use useLocalRuntime
  const modelAdapter = useMemo(() => {
    return createChatModelAdapter(client, {
      modelId: selectedModelId || undefined,
      systemPrompt,
      ephemeral,
      enableReasoning,
      enableArtifacts,
      tools: chatTools,
      onToolCall,
      onChatCreated: handleChatCreated,
      onUserMessage,
      onStreamEnd: handleStreamEnd,
      onBeforeRequest,
      mockResponseOnBlocked,
      onArtifactGenerating: (chatId: string) => onArtifactGeneratingRef.current?.(chatId),
      onArtifactComplete: (chatId: string, artifactId?: string) =>
        onArtifactCompleteRef.current?.(chatId, artifactId),
      surfaceId: errorSurfaceId,
    });
  }, [
    client,
    selectedModelId,
    systemPrompt,
    ephemeral,
    enableReasoning,
    enableArtifacts,
    chatTools,
    onToolCall,
    handleChatCreated,
    onUserMessage,
    handleStreamEnd,
    onBeforeRequest,
    mockResponseOnBlocked,
    errorSurfaceId,
  ]);

  // When loading an existing chat, sync the model selector to that chat's model.
  // We update selectedModelId for the runtime AND call onModelSelectorSync so
  // the parent (WidgetContent) updates its own selector state without triggering
  // the full onModelChange chain (which would navigate away from the chat).
  const handleChatModelResolved = useCallback(
    (chatModelId: string) => {
      setSelectedModelId(chatModelId);
      onModelSelectorSync?.(chatModelId);
    },
    [onModelSelectorSync],
  );

  const initialThreadIdPrevForRefSyncRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = initialThreadIdPrevForRefSyncRef.current;
    initialThreadIdPrevForRefSyncRef.current = initialThreadId;

    if (initialThreadId) {
      currentChatIdRef.current = initialThreadId;
    } else if (prev !== undefined) {
      // Host left a concrete thread (e.g. `/chats/:id` → `/chats`). Without this,
      // `currentChatIdRef` kept the old id and artifact loading still targeted
      // the previous chat while the UI showed the composer.
      currentChatIdRef.current = null;
    }
  }, [initialThreadId]);

  const artifactLoaderContextValue = useMemo(
    () => ({
      client,
      enableArtifacts,
      refreshCallbackRef,
      currentChatIdRef,
      onArtifactCompleteRef,
      initialOpenArtifactId: initialOpenArtifactId?.trim() || null,
      resolvedServerThreadId:
        mode === "singleChat" ? serverThreadId : undefined,
    }),
    [client, enableArtifacts, initialOpenArtifactId, mode, serverThreadId],
  );

  // Source feedback: local rating state + API submission
  const ratingsRef = useRef(new Map<string, SourceRating>());
  const feedbackListenersRef = useRef(new Set<() => void>());

  const sourceFeedbackValue = useMemo<SourceFeedbackContextValue>(() => {
    const notifyFeedbackListeners = () => {
      feedbackListenersRef.current.forEach((l) => l());
    };

    return {
      submitFeedback: (
        chunkId: string,
        rating: SourceRating,
        messageId: string,
      ) => {
        ratingsRef.current.set(chunkId, rating);
        notifyFeedbackListeners();

        const chatId = currentChatIdRef.current;
        if (!chatId) return;

        void client.submitSourceFeedback({
          chatId,
          messageId,
          chunkId,
          rating,
        });
      },
      getRating: (chunkId: string) => ratingsRef.current.get(chunkId),
      subscribe: (listener: () => void) => {
        feedbackListenersRef.current.add(listener);
        return () => {
          feedbackListenersRef.current.delete(listener);
        };
      },
      getSnapshot: () => ratingsRef.current,
    };
  }, [client, currentChatIdRef]);

  // Message-level feedback adapter for useLocalRuntime
  const messageFeedbackAdapter = useMemo(
    () => ({
      submit: ({
        message,
        type,
      }: {
        message: ThreadMessage;
        type: "positive" | "negative";
      }) => {
        const custom = (message.metadata?.custom ?? {}) as Record<
          string,
          unknown
        >;

        let chatId = custom.cuadraChatId as string | undefined;
        let messageId = custom.cuadraServerMessageId as string | undefined;

        if (!chatId) {
          chatId = messageIdToChatIdMap.get(message.id);
        }
        if (!messageId && messageIdToChatIdMap.has(message.id)) {
          messageId = message.id;
        }
        if (!chatId) {
          chatId = currentChatIdRef.current || undefined;
        }

        if (!chatId) return;

        if (messageId) {
          void client.submitMessageFeedback({
            chatId,
            messageId,
            rating: type,
          });
          return;
        }

        // Streamed message without server ID — resolve by fetching the chat
        void (async () => {
          try {
            const chat = await client.getChat(chatId!);
            const assistantMsgs = (chat.messages ?? []).filter(
              (m: { role: string }) => m.role === "assistant",
            );
            const msgText = message.content?.find(
              (p: { type: string }) => p.type === "text",
            ) as { text?: string } | undefined;
            const match =
              assistantMsgs.find(
                (m: { content: string }) => m.content === msgText?.text,
              ) ?? assistantMsgs[assistantMsgs.length - 1];
            if (match) {
              messageIdToChatIdMap.set(message.id, chatId!);
              void client.submitMessageFeedback({
                chatId: chatId!,
                messageId: (match as { id: string }).id,
                rating: type,
              });
            }
          } catch {
            // Fail silently — must not interrupt chat UX
          }
        })();
      },
    }),
    [client, currentChatIdRef],
  );

  const messageFeedbackContextValue = useMemo<MessageFeedbackContextValue>(
    () => ({
      submitComment: (
        chatId: string,
        messageId: string | null,
        comment: string,
        rating: "positive" | "negative",
      ) => {
        if (messageId) {
          void client.submitMessageFeedback({
            chatId,
            messageId,
            rating,
            comment,
          });
          return;
        }

        void (async () => {
          try {
            const chat = await client.getChat(chatId);
            const assistantMsgs = (chat.messages ?? []).filter(
              (m: { role: string }) => m.role === "assistant",
            );
            const lastAssistant = assistantMsgs[assistantMsgs.length - 1] as
              | { id: string }
              | undefined;
            if (lastAssistant) {
              void client.submitMessageFeedback({
                chatId,
                messageId: lastAssistant.id,
                rating,
                comment,
              });
            }
          } catch {
            // Fail silently — must not interrupt chat UX
          }
        })();
      },

      submitSourceFeedback: (
        chatId: string,
        messageId: string | null,
        chunkId: string,
        rating: "positive" | "negative",
      ) => {
        if (messageId) {
          void client.submitSourceFeedback({
            chatId,
            messageId,
            chunkId,
            rating,
          });
          return;
        }

        void (async () => {
          try {
            const chat = await client.getChat(chatId);
            const assistantMsgs = (chat.messages ?? []).filter(
              (m: { role: string }) => m.role === "assistant",
            );
            const lastAssistant = assistantMsgs[assistantMsgs.length - 1] as
              | { id: string }
              | undefined;
            if (lastAssistant) {
              void client.submitSourceFeedback({
                chatId,
                messageId: lastAssistant.id,
                chunkId,
                rating,
              });
            }
          } catch {
            // Fail silently — must not interrupt chat UX
          }
        })();
      },
    }),
    [client],
  );

  if (mode === "singleChat") {
    return (
      <CuadraErrorSurfaceContext.Provider value={errorSurfaceId}>
        <ArtifactLoaderContext.Provider value={artifactLoaderContextValue}>
          <SourceFeedbackContext.Provider value={sourceFeedbackValue}>
            <MessageFeedbackContext.Provider value={messageFeedbackContextValue}>
              <SingleChatProvider
                client={client}
                serverThreadId={serverThreadId}
                modelId={selectedModelId}
                modelAdapter={modelAdapter}
                enableAttachments={enableAttachments}
                enableArtifacts={enableArtifacts}
                supportsVision={supportsVision}
                onChatModelResolved={handleChatModelResolved}
                reloadKey={bgReload.key}
                reloadChatId={bgReload.chatId}
                feedbackAdapter={messageFeedbackAdapter}
                errorSurfaceId={errorSurfaceId}
              >
                {children}
              </SingleChatProvider>
            </MessageFeedbackContext.Provider>
          </SourceFeedbackContext.Provider>
        </ArtifactLoaderContext.Provider>
      </CuadraErrorSurfaceContext.Provider>
    );
  }

  // For multiChat mode, use RemoteThreadListRuntime
  // Pass adapter options instead of the adapter itself so we can add thread ID update callback
  return (
    <CuadraErrorSurfaceContext.Provider value={errorSurfaceId}>
      <ArtifactLoaderContext.Provider value={artifactLoaderContextValue}>
        <SourceFeedbackContext.Provider value={sourceFeedbackValue}>
          <MessageFeedbackContext.Provider value={messageFeedbackContextValue}>
            <MultiChatProvider
              client={client}
              modelAdapterOptions={{
                modelId: selectedModelId || undefined,
                systemPrompt,
                ephemeral,
                enableReasoning,
                enableArtifacts,
                onChatCreated: handleChatCreated,
                onUserMessage,
                onStreamEnd: handleStreamEnd,
                onBeforeRequest,
                mockResponseOnBlocked,
                onArtifactGenerating: (chatId: string) => onArtifactGeneratingRef.current?.(chatId),
                onArtifactComplete: (chatId: string, artifactId?: string) =>
                  onArtifactCompleteRef.current?.(chatId, artifactId),
              }}
              initialThreadId={initialThreadId}
              onChatsLoaded={onChatsLoaded}
              onThreadIdUpdate={handleThreadIdUpdate}
              onError={onError}
              enableAttachments={enableAttachments}
              supportsVision={supportsVision}
              feedbackAdapter={messageFeedbackAdapter}
              errorSurfaceId={errorSurfaceId}
            >
              {children}
            </MultiChatProvider>
          </MessageFeedbackContext.Provider>
        </SourceFeedbackContext.Provider>
      </ArtifactLoaderContext.Provider>
    </CuadraErrorSurfaceContext.Provider>
  );
}

/**
 * Internal provider for singleChat mode using useLocalRuntime.
 *
 * Reacts to `initialThreadId` prop changes without requiring the parent
 * to change the `key` prop on CuadraChat.  Only the inner
 * `SingleChatRuntime` is remounted (via an internal key) when the
 * fetched messages are ready, keeping the outer chrome (border, theme,
 * etc.) completely stable across thread switches.
 *
 * Uses a "deferred swap" pattern: the currently-displayed content stays
 * visible while the next thread is being fetched.  A spinner is shown
 * only on the very first load (when there is no prior content).
 */
function SingleChatProvider({
  client,
  serverThreadId,
  modelId,
  modelAdapter,
  enableAttachments,
  enableArtifacts,
  supportsVision = true,
  onChatModelResolved,
  reloadKey,
  reloadChatId,
  feedbackAdapter,
  errorSurfaceId,
  children,
}: {
  client: CuadraChatClient;
  serverThreadId?: string;
  modelId?: string | null;
  modelAdapter: ReturnType<typeof createChatModelAdapter>;
  enableAttachments?: boolean;
  enableArtifacts?: boolean;
  supportsVision?: boolean;
  onChatModelResolved?: (modelId: string) => void;
  reloadKey?: number;
  reloadChatId?: string | null;
  feedbackAdapter?: MessageFeedbackAdapter;
  errorSurfaceId: string;
  children: React.ReactNode;
}) {
  const { committed, isFirstLoad } = useSingleChatLoader({
    client,
    serverThreadId,
    modelId,
    onChatModelResolved,
    enableArtifacts,
    reloadKey,
    reloadChatId,
  });

  if (isFirstLoad) {
    return (
      <SingleChatRuntime
        key="__first_load__"
        modelAdapter={modelAdapter}
        enableAttachments={enableAttachments}
        supportsVision={supportsVision}
        feedbackAdapter={feedbackAdapter}
        errorSurfaceId={errorSurfaceId}
      >
        <ThreadLoadingIndicator />
      </SingleChatRuntime>
    );
  }

  // Render with committed state.  On subsequent thread switches the old
  // content stays visible until the fetch completes and commits.
  return (
    <SingleChatRuntime
      key={committed.key}
      modelAdapter={modelAdapter}
      initialMessages={committed.messages}
      enableAttachments={enableAttachments}
      supportsVision={supportsVision}
      feedbackAdapter={feedbackAdapter}
      errorSurfaceId={errorSurfaceId}
    >
      {children}
    </SingleChatRuntime>
  );
}

/** Minimal loading indicator shown only during the very first thread load. */
function ThreadLoadingIndicator() {
  return (
    <div
      className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full cuadra-w-full"
      role="status"
      aria-busy="true"
      aria-label="Loading thread"
    >
      <div
        className="cuadra-h-6 cuadra-w-6 cuadra-animate-spin cuadra-rounded-full cuadra-border-2 cuadra-border-muted-foreground/30 cuadra-border-t-muted-foreground"
        aria-hidden="true"
      />
    </div>
  );
}

/**
 * Thin wrapper that calls useLocalRuntime with the resolved initialMessages.
 * Separated so that SingleChatProvider can delay rendering until the fetch
 * completes, keeping hooks unconditional.
 */
function SingleChatRuntime({
  modelAdapter,
  initialMessages,
  enableAttachments,
  supportsVision = true,
  feedbackAdapter,
  errorSurfaceId,
  children,
}: {
  modelAdapter: ReturnType<typeof createChatModelAdapter>;
  initialMessages?: ThreadMessageLike[];
  enableAttachments?: boolean;
  supportsVision?: boolean;
  feedbackAdapter?: MessageFeedbackAdapter;
  errorSurfaceId: string;
  children: React.ReactNode;
}) {
  // Create attachment adapter only if enabled
  const attachmentAdapter = useMemo(() => {
    return enableAttachments
      ? createAttachmentAdapter({
          supportsVision,
          onValidationError: (error: AttachmentValidationError) => {
            attachmentErrorStore.push(error.message, error.code, errorSurfaceId);
          },
        })
      : undefined;
  }, [enableAttachments, supportsVision, errorSurfaceId]);

  const localRuntime = useLocalRuntime(modelAdapter, {
    initialMessages,
    adapters: {
      ...(attachmentAdapter && { attachments: attachmentAdapter }),
      ...(feedbackAdapter && { feedback: feedbackAdapter }),
    },
  });

  return (
    <AssistantRuntimeProvider runtime={localRuntime}>
      {children}
    </AssistantRuntimeProvider>
  );
}

/**
 * Internal provider for multiChat mode using RemoteThreadListRuntime
 */
function MultiChatProvider({
  client,
  modelAdapterOptions,
  initialThreadId: _initialThreadId,
  onChatsLoaded,
  onThreadIdUpdate,
  onError: _onError,
  enableAttachments,
  supportsVision,
  feedbackAdapter,
  errorSurfaceId,
  children,
}: {
  client: CuadraChatClient;
  modelAdapterOptions: {
    modelId?: string | null;
    systemPrompt?: string;
    ephemeral?: boolean;
    enableReasoning?: boolean;
    enableArtifacts?: boolean;
    onChatCreated?: (chatId: string) => void;
    onUserMessage?: () => void;
    onStreamEnd?: (chatId: string) => void;
    onBeforeRequest?: () => boolean | Promise<boolean>;
    mockResponseOnBlocked?: string | (() => string);
    onArtifactGenerating?: (chatId: string) => void;
    onArtifactComplete?: (chatId: string, artifactId?: string) => void;
  };
  initialThreadId?: string;
  onChatsLoaded?: () => void;
  onThreadIdUpdate?: (oldId: string, newId: string) => void;
  onError?: (error: Error) => void;
  enableAttachments?: boolean;
  supportsVision?: boolean;
  feedbackAdapter?: MessageFeedbackAdapter;
  errorSurfaceId: string;
  children: React.ReactNode;
}) {
  // Create thread list adapter
  const enableArtifacts = modelAdapterOptions.enableArtifacts ?? false;
  const threadListAdapter = useMemo(() => {
    return createThreadListAdapter(
      client,
      (localThreadId, serverChatId) => {
        onThreadIdUpdate?.(localThreadId, serverChatId);
      },
      enableArtifacts,
    );
  }, [client, onThreadIdUpdate, enableArtifacts]);

  // Create a callback to update thread ID mapping when chat is created
  const handleThreadIdUpdateFromAdapter = useCallback(
    (localThreadId: string, serverChatId: string) => {
      // Update the adapter's remote ID mapping
      if (threadListAdapter.updateRemoteId) {
        threadListAdapter.updateRemoteId(localThreadId, serverChatId);
      }
      // Also call the prop callback
      onThreadIdUpdate?.(localThreadId, serverChatId);
    },
    [threadListAdapter, onThreadIdUpdate],
  );

  // Use a ref for onChatsLoaded so wrappedAdapter stays stable
  const onChatsLoadedRef = useRef(onChatsLoaded);
  onChatsLoadedRef.current = onChatsLoaded;

  // Wrap list method to call onChatsLoaded (stable — only depends on threadListAdapter)
  const wrappedAdapter = useMemo(() => {
    return {
      ...threadListAdapter,
      async list() {
        const result = await threadListAdapter.list();
        onChatsLoadedRef.current?.();
        return result;
      },
    };
  }, [threadListAdapter]);

  // Expose adapter globally for external access (e.g., rename/delete)
  useEffect(() => {
    (
      window as Window & { __cuadraThreadListAdapter?: typeof wrappedAdapter }
    ).__cuadraThreadListAdapter = wrappedAdapter;
    (
      window as Window & { __cuadraThreadListRuntime?: unknown }
    ).__cuadraThreadListRuntime = null;
  }, [wrappedAdapter]);

  // Keep modelId in a ref so changing it doesn't recreate the adapter
  // (which would rebuild the runtime and lose the current thread).
  // The adapter reads the ref lazily at request time via getModelId.
  const modelIdRef = useRef(modelAdapterOptions.modelId);
  modelIdRef.current = modelAdapterOptions.modelId;

  // Stable options that exclude modelId (modelId is read via ref)
  const stableAdapterOptions = useMemo(() => {
    const { modelId: _modelId, ...rest } = modelAdapterOptions;
    return rest;
  }, [
    modelAdapterOptions.systemPrompt,
    modelAdapterOptions.ephemeral,
    modelAdapterOptions.enableReasoning,
    modelAdapterOptions.enableArtifacts,
    modelAdapterOptions.onChatCreated,
    modelAdapterOptions.onUserMessage,
    modelAdapterOptions.onStreamEnd,
    modelAdapterOptions.onBeforeRequest,
    modelAdapterOptions.mockResponseOnBlocked,
    modelAdapterOptions.onArtifactGenerating,
    modelAdapterOptions.onArtifactComplete,
  ]);

  // Create model adapter with thread ID update callback for multiChat mode
  const modelAdapter = useMemo(() => {
    return createChatModelAdapter(client, {
      ...stableAdapterOptions,
      getModelId: () => modelIdRef.current,
      onThreadIdUpdate: handleThreadIdUpdateFromAdapter,
      surfaceId: errorSurfaceId,
    });
  }, [client, stableAdapterOptions, handleThreadIdUpdateFromAdapter, errorSurfaceId]);

  // Create attachment adapter only if enabled
  const attachmentAdapter = useMemo(() => {
    return enableAttachments
      ? createAttachmentAdapter({
          supportsVision,
          onValidationError: (error: AttachmentValidationError) => {
            attachmentErrorStore.push(error.message, error.code, errorSurfaceId);
          },
        })
      : undefined;
  }, [enableAttachments, supportsVision, errorSurfaceId]);

  // Create runtime hook that uses the model adapter
  const runtimeHook = useCallback(() => {
    return useLocalRuntime(modelAdapter, {
      adapters: {
        ...(attachmentAdapter && { attachments: attachmentAdapter }),
        ...(feedbackAdapter && { feedback: feedbackAdapter }),
      },
    });
  }, [modelAdapter, attachmentAdapter, feedbackAdapter]);

  // Use RemoteThreadListRuntime hook
  const runtime = unstable_useRemoteThreadListRuntime({
    adapter: wrappedAdapter,
    runtimeHook,
  });

  // Expose runtime globally for external refresh
  useEffect(() => {
    (
      window as Window & { __cuadraThreadListRuntime?: typeof runtime }
    ).__cuadraThreadListRuntime = runtime;
  }, [runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
